// src/pages/FAQ.tsx (or SupportPage.tsx)
import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Footer from '../components/footer/footer';
import Header from '../components/header/header';
import { APP_STORE_URL } from '../constants/links';

// Reuse the same color constants as landing page
const AUREUS = {
  green: '#6FC2AE',
  blue: '#609EBD',
  navy: '#01263D',
  purple: '#4643D3',
};

// Simple fade-in variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

// FAQ data (you can expand this)
const faqData = [
  {
    question: "What is Oxen Flow?",
    answer:
      "Oxen Flow is a mobile application that allows users to manage their digital assets on Flare Network seamlessly, with an intuitive interface for a smooth experience. Enjoy one-tap passkey login, tradable usernames, effortless yield, and true self-custody.",
  },
  {
    question: "Who builds Oxen Flow?",
    answer:
      "Oxen Flow is built and curated by Aureus Ox — the same team that runs production Flare and XRPL infrastructure. Learn more at aureusox.com.",
  },
  {
    question: "How do I sign up for Oxen Flow?",
    answer:
      "Download the app from the App Store (currently iOS-only). Tap 'Register' and follow the one-step passkey registration — no seed phrases required.",
  },
  {
    question: "Is Oxen Flow Non-Custodial?",
    answer:
      "Yes — 100% non-custodial. You control your private keys at all times. Oxen Flow uses secure Web3Auth + TSS under the hood and has no access to your key material.",
  },
  {
    question: "Is my information secure on Oxen Flow?",
    answer:
      "Security is our top priority. We use advanced encryption, passkeys/biometrics, and have been audited by FYEO. We collect minimal data — your email is only used for passkey support. Read the public FYEO audit: https://github.com/fyeo-io/public-audit-reports/blob/main/Code%20Audit%20Reports/2024/Aureus%20OX/Aureus%20Ox%20-%20Security%20Code%20Review%20Aureus%20Ox%20v1.0_public.pdf",
  },
  {
    question: "Are there any fees associated with using Oxen Flow?",
    answer:
      "Oxen Flow itself is free to use. Only standard blockchain transaction fees apply when sending assets or interacting with the chain.",
  },
  {
    question: "Who can I contact for support?",
    answer:
      "Reach out to our team at info@oxenflow.io. We're here to help! You can also check our tutorials on YouTube or follow us on X for updates.",
  },
  {
    question: "How do I mint FXRP in Oxen Flow?",
    answer: "Deposit or send XRP to your in-app address → Tap \"Bridge to Flare XRP\" on the homepage → Wait a moment — FXRP appears after a confirmations are made. One-tap magic."
  },
  {
    question: "How do I redeem FXRP back to XRP?",
    answer: "Select FXRP → Choose redeem → Confirm — XRP credits to your XRPL address. Minimum 5 XRP."
  },
  {
    question: "Why mint FXRP with Oxen Flow?",
    answer: "Unlike complex dApps requiring multiple wallets/connections, Oxen Flow handles everything natively — secure, fast, mobile-first, and fully non-custodial. Longer walkthrough: https://oxenflow.io/guides/what-is-fxrp"
  },
  {
    question: "What is FXRP?",
    answer:
      "FXRP is Flare’s FAsset for XRP — a 1:1 representation of XRP on Flare you can mint, use in DeFi, and redeem back to XRP. Full guide: https://oxenflow.io/guides/what-is-fxrp"
  },
  {
    question: "Is there a desktop Oxen Flow wallet?",
    answer:
      "No. Oxen Flow is iOS-only. The official product is the App Store app and oxenflow.io. Do not install third-party “desktop Oxen Flow” downloads."
  }
];

const FAQItem: FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="rounded-2xl overflow-hidden border"
      style={{
        background: 'rgba(1, 38, 61, 0.45)',
        borderColor: 'rgba(96, 158, 189, 0.25)',
      }}
      variants={fadeInUp}
      whileHover={{
        borderColor: AUREUS.green,
        boxShadow: `0 8px 30px rgba(${parseInt(AUREUS.green.slice(1,3),16)},${parseInt(AUREUS.green.slice(3,5),16)},${parseInt(AUREUS.green.slice(5,7),16)},0.2)`,
      }}
      transition={{ duration: 0.3 }}
    >
      <button
        className="w-full px-8 py-6 text-left flex items-center justify-between transition-all"
        onClick={() => setIsOpen(!isOpen)}
        style={{ background: isOpen ? 'rgba(96, 158, 189, 0.08)' : 'transparent' }}
      >
        <h3
          className="text-xl md:text-2xl font-bold"
          style={{ color: isOpen ? AUREUS.green : 'white' }}
        >
          {question}
        </h3>
        <ChevronDownIcon
          className="h-7 w-7 transition-transform duration-300"
          style={{
            color: AUREUS.blue,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <div className="px-8 pb-8 pt-2">
              <p className="text-base md:text-lg opacity-90 leading-relaxed" style={{ color: AUREUS.blue }}>
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQPage: FC = () => {
  return (
    <div
      className="min-h-screen text-white pb-20"
      style={{
        background: `linear-gradient(to bottom, ${AUREUS.navy}, #0a1a2f)`,
      }}
    >
      {/* Navigation - assuming it matches landing page */}
      <Header /> {/* ← Reuse your landing page Navigation component */}

      <div className="mx-auto max-w-5xl px-6 pt-32 md:pt-44">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-5xl md:text-6xl font-black tracking-tight mb-6 bg-clip-text"
            style={{
              background: `linear-gradient(90deg, ${AUREUS.green}, ${AUREUS.blue}, ${AUREUS.purple})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            variants={fadeInUp}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            className="text-xl opacity-80 max-w-2xl mx-auto"
            variants={fadeInUp}
          >
            Quick answers to the most common questions about Oxen Flow
          </motion.p>
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          className="space-y-6"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {faqData.map((item, index) => (
            <FAQItem key={index} question={item.question} answer={item.answer} />
          ))}
        </motion.div>

        {/* Contact + download CTAs */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-lg opacity-80 mb-6">
            Still have questions?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:info@oxenflow.io"
              className="inline-block px-10 py-5 rounded-2xl font-semibold text-lg shadow-2xl transition-all hover:scale-105"
              style={{
                background: `linear-gradient(90deg, ${AUREUS.green}, ${AUREUS.blue})`,
                color: AUREUS.navy,
              }}
            >
              Contact Support →
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-5 rounded-2xl font-semibold text-lg transition-all hover:scale-105"
              style={{
                background: 'rgba(70, 67, 211, 0.12)',
                border: `1px solid rgba(96, 158, 189, 0.35)`,
                color: AUREUS.blue,
              }}
            >
              Download on the App Store →
            </a>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;